import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-587ac6c4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "scan-modal" }

import { onMounted, onBeforeUnmount, onErrorCaptured, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import ScanModalHeader from "./scan-modal-header/ScanModalHeader.vue";
import ScanInput from "./scan-input/ScanInput.vue";
import CodeScanner from "./code-scanner/CodeScanner.vue";
import ScanModalMessages from "./scan-modal-messages/ScanModalMessages.vue";
import { ScanModalError, ScanAndGoErrorType } from "./errors";
import { SCAN_ERROR_MESSAGES, ROUTE_NAMES } from "../../../utils";
import AppSpinner from "../../spinner/AppSpinner.vue";
import { trackCartAction, trackFailedScanAndGoAdd } from "@/utils/pagesTrack";
import { AddItemSources, ScanAndGoRejectReasons, TrackEventField } from "@/models/utils/interfaces/pages-track";
import { isItemAvailableDayParts } from "../../../store/modules/vendor/menu-items";

// consts

export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const MANUAL_INPUT_SOURCE = "MANUAL_INPUT_SOURCE";
const SCANNER_SOURCE = "SCANNER_SOURCE";

// Template refs
const scanModalContainer = ref<HTMLDivElement>();
const scanInput = ref<typeof ScanInput>();

const defaultErrorMesage: ScanAndGoErrorType = {
  header: "",
  body: ""
};

// State
const menuItem = ref<any>();
const menuItemAdded = ref<any>();
const lastCodeAdded = ref("");
const isFetchingItem = ref(false);
const isLoadingCamera = ref(true);
const errorMessage = ref(defaultErrorMesage);
const dialogMessage = ref("");
const dialogTitle = ref("");
const isManualInputEnabled = ref<boolean>(false);
const scanningEnabled = ref<boolean>(true);
const codeSource = ref<string>(SCANNER_SOURCE);

const store = useStore();
const router = useRouter();

let dialogCloseCallback: (() => void) | null;

const getItemByScannedCode = async (code: string) => {
  try {
    isFetchingItem.value = true;
    const item = await store.dispatch("vendor/fetchVendorItemByExternalId", code);
    isFetchingItem.value = false;
    return item;
  } catch (error) {
    isFetchingItem.value = false;
    return null;
  }
};

const hideModal = () => {
  store.dispatch("modals/hideModal", null, { root: true });
};

const goToCart = () => {
  hideModal();
  router.push({ name: ROUTE_NAMES.SHOPPING_CART });
};

const clearPreviousScanning = () => {
  errorMessage.value = defaultErrorMesage;
  menuItem.value = null;
  menuItemAdded.value = null;
  lastCodeAdded.value = "";
  dialogTitle.value = "";
  dialogMessage.value = "";
  scanningEnabled.value = true;
  dialogCloseCallback = null;
};

const onItemAddedMessageClosed = () => {
  clearPreviousScanning();
};

const updateModalStyle = () => {
  if (scanModalContainer?.value?.style) scanModalContainer.value.style.maxHeight = `${window.innerHeight}px`;
};

onMounted(() => {
  // handle resize
  window.addEventListener("resize", updateModalStyle);
  updateModalStyle();

  isManualInputEnabled.value = store.getters["vendor/isManualInputEnabled"];
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateModalStyle);
});

// Error handling
onErrorCaptured(error => {
  if (error instanceof ScanModalError) {
    errorMessage.value.header = error.message;

    // Prevent the error from propagating further.
    return false;
  }
});

const onErrorMessageClosed = () => {
  errorMessage.value = defaultErrorMesage;
};

const onDialogClosed = () => {
  if (dialogCloseCallback) dialogCloseCallback();
};

const addToCartDirectly = async () => {
  const payload = {
    item: menuItem.value,
    quantity: 1,
    specialInstructions: "",
    trackEvent: false
  };
  await store.dispatch("cart/addItemToCart", payload);

  errorMessage.value = defaultErrorMesage;
  menuItemAdded.value = menuItem.value;

  trackCartAction(TrackEventField.ADD, menuItem.value, {}, 1, getSourceForTrackEvent());

  if (menuItem.value.isAlcohol) {
    const alcoholRestrictionMessage = store.getters["vendor/getAlcoholRestrictionMessage"];

    dialogTitle.value = "";
    dialogMessage.value = alcoholRestrictionMessage;
    dialogCloseCallback = () => {
      dialogMessage.value = "";
      dialogCloseCallback = null;
    };
  }
};

const tryAddtoCart = () => {
  const hasModifiers = menuItem.value?.hasModifiers;
  const isAlcoholEnabled = store.getters["vendor/isAlcoholEnabled"];
  const alcoholIsNotAllowed = menuItem.value.isAlcohol && !isAlcoholEnabled;
  const isAlcoholLimitReached = store.getters["cart/isAlcoholLimitReached"];
  const isActive = !menuItem.value.soldOut && menuItem.value.isActive;

  if (!isActive) {
    errorMessage.value = SCAN_ERROR_MESSAGES.notAvailable;
    scanningEnabled.value = false;
    menuItem.value = null;
    trackFailedScanAndGoAdd(lastCodeAdded.value, getSourceForTrackEvent(), ScanAndGoRejectReasons.ITEM_INACTIVE)

    return;
  }

  if (hasModifiers) {
    errorMessage.value = SCAN_ERROR_MESSAGES.cannotBeAdded;
    menuItem.value = null;
    scanningEnabled.value = false;
    trackFailedScanAndGoAdd(lastCodeAdded.value, getSourceForTrackEvent(), ScanAndGoRejectReasons.ITEM_WITH_MODIFIERS)

    return;
  }
  if (alcoholIsNotAllowed) {
    errorMessage.value = SCAN_ERROR_MESSAGES.alcoholNotPermitted;
    scanningEnabled.value = false;
    menuItem.value = null;
    trackFailedScanAndGoAdd(lastCodeAdded.value, getSourceForTrackEvent(), ScanAndGoRejectReasons.ALCOHOL_NOT_ALLOWED)

    return;
  }

  if (menuItem.value.isAlcohol && isAlcoholLimitReached()) {
    const alcoholLimitMessagePerOrder = store.getters["vendor/getOrderAlcoholLimitMessage"];

    dialogTitle.value = "Warning";
    dialogMessage.value = alcoholLimitMessagePerOrder;
    scanningEnabled.value = false
    trackFailedScanAndGoAdd(lastCodeAdded.value, getSourceForTrackEvent(), ScanAndGoRejectReasons.ALCOHOL_LIMIT_REACHED)

    dialogCloseCallback = () => {
      clearPreviousScanning();
    };
    return;
  }

  const activeDayParts = store.getters["dayParts/getDayPartsBySelectedTime"]();
  if (!isItemAvailableDayParts(menuItem.value, activeDayParts)) {
    errorMessage.value = SCAN_ERROR_MESSAGES.unavailableDayPart;
    scanningEnabled.value = false;
    menuItem.value = null;
    trackFailedScanAndGoAdd(lastCodeAdded.value, getSourceForTrackEvent(), ScanAndGoRejectReasons.ITEM_DAYPARTS_UNAVAILABLE)

    return;
  }

  addToCartDirectly();
};

const onCameraEnabled = async () => {
  isLoadingCamera.value = false;
};

const onCodeFound = async (code: string) => {
  // If a previous scan is fetching response or scanning is not enabled reject.
  if (isFetchingItem.value || !scanningEnabled.value) return;

  //The condition prevents multiple adds for the same item.
  if (!menuItem.value || lastCodeAdded.value !== code) {
    clearPreviousScanning();
    const item = await getItemByScannedCode(code);
    if (item) {
      menuItem.value = item;
      lastCodeAdded.value = code;
      tryAddtoCart();
    } else {
      errorMessage.value = SCAN_ERROR_MESSAGES.noMatches;
      scanningEnabled.value = false;
      trackFailedScanAndGoAdd(code, getSourceForTrackEvent() , ScanAndGoRejectReasons.INVALID_BARCODE)
    }
  }
};

const onManualCodeSubmitted = async (code: string) => {
  codeSource.value = MANUAL_INPUT_SOURCE;

  //set focus to scanModalContainer to let the user scan again
  scanModalContainer.value?.focus();
  scanningEnabled.value = true;

  await onCodeFound(code);
}

const onScannerCodeSubmitted = async (code: string) => {
  codeSource.value = SCANNER_SOURCE;

  await onCodeFound(code);
}

const onTryAgain = () => {
  errorMessage.value = defaultErrorMesage;
  if (codeSource.value === MANUAL_INPUT_SOURCE) {
    scanInput.value?.focusInput()
    scanningEnabled.value = false;
    return;
  }
  scanModalContainer.value?.focus();
  scanningEnabled.value = true;
}

const getSourceForTrackEvent = (): AddItemSources => {
  return codeSource.value === MANUAL_INPUT_SOURCE ? AddItemSources.SCAN_MANUAL_INPUT: AddItemSources.SCAN;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "scanModalContainer",
      ref: scanModalContainer,
      tabIndex: "0",
      class: "scan-modal__container"
    }, [
      _createVNode(AppSpinner),
      _createVNode(ScanModalHeader, { onCloseClick: hideModal }),
      (isManualInputEnabled.value)
        ? (_openBlock(), _createBlock(ScanInput, {
            key: 0,
            ref_key: "scanInput",
            ref: scanInput,
            onManualCodeSubmitted: onManualCodeSubmitted,
            onFocusout: _cache[0] || (_cache[0] = ($event: any) => (scanningEnabled.value = true)),
            onFocusin: _cache[1] || (_cache[1] = ($event: any) => (scanningEnabled.value = false))
          }, null, 512))
        : _createCommentVNode("", true),
      _createVNode(CodeScanner, {
        onCodeFound: onScannerCodeSubmitted,
        onCameraEnabled: onCameraEnabled
      }),
      _createVNode(ScanModalMessages, {
        "is-scanning": !isFetchingItem.value && !menuItem.value && !isLoadingCamera.value && scanningEnabled.value,
        "is-fetching-item": isFetchingItem.value,
        "menu-item-added": menuItemAdded.value,
        "is-loading-camera": isLoadingCamera.value && !errorMessage.value,
        "error-message": errorMessage.value,
        "dialog-title": dialogTitle.value,
        "dialog-message": dialogMessage.value,
        onErrorCloseClick: onErrorMessageClosed,
        onItemAddedCloseClick: onItemAddedMessageClosed,
        onViewCartClick: goToCart,
        onDialogCloseClick: onDialogClosed,
        onTryAgainClick: onTryAgain
      }, null, 8, ["is-scanning", "is-fetching-item", "menu-item-added", "is-loading-camera", "error-message", "dialog-title", "dialog-message"])
    ], 512)
  ]))
}
}

})